import type { CanonicalElementPayload } from '../../mocks/canonicalMocks';
import type { CanonicalPackagePayload } from './types';

const PACKAGE_SCHEMA_VERSION = '1.0.0';
const CEM_VERSION = '1.0';
const QUANTITY_UNITS = new Set(['m', 'mm', 's', 'min', 'kg', 'N', 'Pa', 'deg', 'rad']);

function toPackageProperty(value: unknown): unknown {
    if (
        value === null
        || typeof value === 'string'
        || typeof value === 'number'
        || typeof value === 'boolean'
    ) {
        return value;
    }
    if (Array.isArray(value)) {
        const converted = value.map(toPackageProperty);
        return converted.every(item => item !== undefined) ? converted : undefined;
    }
    if (value && typeof value === 'object') {
        const candidate = value as Record<string, unknown>;
        if (
            typeof candidate.value === 'number'
            && typeof candidate.unit === 'string'
            && QUANTITY_UNITS.has(candidate.unit)
        ) {
            return { value: candidate.value, unit: candidate.unit };
        }
        if (typeof candidate.$ref === 'string') {
            return { $ref: candidate.$ref };
        }
    }
    return undefined;
}

function toPackageProperties(
    properties: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
    return Object.fromEntries(
        Object.entries(properties)
            .map(([name, value]) => [name, toPackageProperty(value)] as const)
            .filter((entry): entry is readonly [string, unknown] => entry[1] !== undefined),
    );
}

export function buildCanonicalPackage(
    elementType: 'wall' | 'stairs' | 'ramp',
    element: CanonicalElementPayload,
): CanonicalPackagePayload {
    const entityType = elementType === 'wall' ? 'Wall' : elementType === 'stairs' ? 'Stair' : 'Ramp';
    const catalogueEntityId = elementType === 'wall'
        ? 'cem.entity.wall'
        : elementType === 'stairs'
            ? 'cem.entity.stair'
            : 'cem.entity.ramp';
    const stableId = element.id.replace(/[^A-Za-z0-9._:-]/g, '-');

    return {
        ...element,
        schemaVersion: PACKAGE_SCHEMA_VERSION,
        catalogueId: 'bimcomply.canonical-entities',
        catalogueVersion: '1.0.0',
        packageId: `pkg-web-debug-${stableId}`,
        runId: `run-web-debug-${stableId}`,
        cemVersion: CEM_VERSION,
        apiVersion: 'v2',
        clientCapabilities: {
            geometryLevels: ['scalar'],
            scopes: ['element'],
            streaming: true,
            hostId: 'web-plugin-debug',
            extractorVersion: '1.0.0',
        },
        scope: {
            requested: 'element',
            focusElements: [element.id],
        },
        entities: [
            {
                id: element.id,
                catalogueEntityId,
                type: entityType,
                cemVersion: CEM_VERSION,
                provenance: {
                    sourceHost: 'web-plugin-debug',
                    sourceElementId: element.id,
                    sourceVersion: '1.0.0',
                },
                properties: toPackageProperties(element.properties),
            },
        ],
        relationships: [],
    };
}
